/* ─── RothIRAGuides Theme JS ─────────────────────────────────────────────── */

/* ─── Utilities ──────────────────────────────────────────────────────────── */
const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

function formatMoney(cents) {
  return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
}

function showToast(msg, duration = 3200) {
  const container = $('#ToastContainer');
  if (!container) return;
  const el = document.createElement('div');
  el.className = 'toast';
  el.setAttribute('role', 'alert');
  el.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>${msg}`;
  container.appendChild(el);
  setTimeout(() => {
    el.classList.add('out');
    el.addEventListener('animationend', () => el.remove(), { once: true });
  }, duration);
}

/* ─── Cart AJAX API ──────────────────────────────────────────────────────── */
const Cart = {
  async get() {
    const r = await fetch('/cart.js');
    return r.json();
  },

  async add(variantId, quantity = 1) {
    const r = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: variantId, quantity })
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      throw new Error(err.description || 'Could not add item to cart.');
    }
    return r.json();
  },

  // Checks the live cart first; if the variant already exists, increments its
  // quantity via /cart/change.js instead of creating a duplicate line item.
  async addOrIncrement(variantId, quantity = 1) {
    const cart = await Cart.get();
    const vid = String(variantId);
    const existing = cart.items.find(item => String(item.variant_id) === vid);

    if (existing) {
      return Cart.change(existing.key, existing.quantity + quantity);
    }
    return Cart.add(variantId, quantity);
  },

  async change(key, quantity) {
    const r = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: key, quantity })
    });
    return r.json();
  },

  async remove(key) {
    return Cart.change(key, 0);
  }
};

/* ─── Cart Drawer ────────────────────────────────────────────────────────── */
const CartDrawer = {
  el: null,
  overlay: null,

  init() {
    this.el      = $('#CartDrawer');
    this.overlay = $('#CartOverlay');
    if (!this.el) return;

    // Drawer starts hidden — make its focusable children unreachable
    this.el.querySelectorAll('a, button').forEach(el => el.setAttribute('tabindex', '-1'));

    $('#CartDrawerClose')?.addEventListener('click', () => this.close());
    $('#CartDrawerContinue')?.addEventListener('click', () => this.close());
    this.overlay.addEventListener('click', () => this.close());

    $('#CartBrowseBtn')?.addEventListener('click', e => {
      e.preventDefault();
      this.close();
      const href = e.currentTarget.getAttribute('href');
      setTimeout(() => {
        if (href.startsWith('#')) {
          const target = document.getElementById(href.slice(1));
          if (target) target.scrollIntoView({ behavior: 'smooth' });
        } else {
          window.location.href = href;
        }
      }, 50);
    });

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.el.classList.contains('open')) this.close();
    });

    // Remove buttons (delegated)
    this.el.addEventListener('click', async e => {
      const removeBtn = e.target.closest('[data-remove-key]');
      if (removeBtn) {
        removeBtn.disabled = true;
        await Cart.remove(removeBtn.dataset.removeKey);
        await this.refresh();
        return;
      }

      // Quantity stepper buttons (delegated)
      const qtyBtn = e.target.closest('[data-qty-key]');
      if (qtyBtn) {
        qtyBtn.disabled = true;
        const key   = qtyBtn.dataset.qtyKey;
        const delta = parseInt(qtyBtn.dataset.qtyDelta, 10);
        const cart  = await Cart.get();
        const item  = cart.items.find(i => i.key === key);
        if (item) {
          const newQty = item.quantity + delta;
          if (newQty <= 0) {
            await Cart.remove(key);
          } else {
            await Cart.change(key, newQty);
          }
        }
        await this.refresh();
      }
    });
  },

  async open() {
    await this.refresh();
    this.el.classList.add('open');
    this.overlay.classList.add('open');
    this.el.setAttribute('aria-hidden', 'false');
    this.overlay.setAttribute('aria-hidden', 'false');
    this.el.querySelectorAll('a, button').forEach(el => el.removeAttribute('tabindex'));
    document.body.style.overflow = 'hidden';
  },

  close() {
    this.el.classList.remove('open');
    this.overlay.classList.remove('open');
    this.el.setAttribute('aria-hidden', 'true');
    this.overlay.setAttribute('aria-hidden', 'true');
    this.el.querySelectorAll('a, button').forEach(el => el.setAttribute('tabindex', '-1'));
    document.body.style.overflow = '';
  },

  async refresh() {
    const cart = await Cart.get();
    this._render(cart);
    this._updateBadge(cart.item_count);
  },

  _render(cart) {
    const itemsEl  = $('#CartDrawerItems');
    const emptyEl  = $('#CartDrawerEmpty');
    const footerEl = $('#CartDrawerFooter');
    const totalEl  = $('#CartDrawerTotal');

    if (!itemsEl) return;

    if (cart.item_count === 0) {
      itemsEl.innerHTML = '';
      emptyEl?.removeAttribute('hidden');
      footerEl?.setAttribute('hidden', '');
      return;
    }

    emptyEl?.setAttribute('hidden', '');
    footerEl?.removeAttribute('hidden');
    if (totalEl) totalEl.textContent = formatMoney(cart.total_price);

    itemsEl.innerHTML = cart.items.map(item => {
      const img = item.image
        ? `<img src="${item.image}" alt="${item.product_title}" loading="lazy" width="52" height="70"/>`
        : `<div class="cart-item__cover-placeholder">${item.product_title.charAt(0)}</div>`;

      const linePrice = item.quantity > 1
        ? `${formatMoney(item.final_line_price)} <span class="cart-item__unit-price">(${formatMoney(item.final_price)} each)</span>`
        : formatMoney(item.final_line_price);

      return `
      <div class="cart-item" data-key="${item.key}">
        <div class="cart-item__cover">${img}</div>
        <div class="cart-item__info">
          <div class="cart-item__title">${item.product_title}</div>
          <div class="cart-item__price">${linePrice}</div>
          <div class="cart-item__qty-row">
            <button class="cart-item__qty-btn" data-qty-key="${item.key}" data-qty-delta="-1"
                    aria-label="Decrease quantity of ${item.product_title}">−</button>
            <span class="cart-item__qty" aria-label="Quantity: ${item.quantity}">${item.quantity}</span>
            <button class="cart-item__qty-btn" data-qty-key="${item.key}" data-qty-delta="1"
                    aria-label="Increase quantity of ${item.product_title}">+</button>
          </div>
        </div>
        <button class="cart-item__remove" data-remove-key="${item.key}" aria-label="Remove ${item.product_title}">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>`;
    }).join('');
  },

  _updateBadge(count) {
    $$('.cart-badge').forEach(b => {
      b.textContent = count;
      b.classList.toggle('visible', count > 0);
    });
  }
};

/* ─── Add to Cart buttons ────────────────────────────────────────────────── */
async function handleAddToCart(btn) {
  // If already in "In Cart" state, just reopen the drawer — never add again
  if (btn.dataset.inCart) {
    CartDrawer.open();
    return;
  }

  const variantId = btn.dataset.variantId;
  if (!variantId) return;

  const originalHTML = btn.innerHTML;
  btn.disabled = true;
  btn.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" class="spin" aria-hidden="true"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg> Adding…`;

  try {
    await Cart.addOrIncrement(variantId);
    await CartDrawer.refresh();

    const isMainSubmit = btn.classList.contains('product-form__submit') || btn.classList.contains('sticky-buy__btn');

    if (isMainSubmit) {
      window.location.href = '/checkout';
    } else {
      btn.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg> Added!`;
      btn.style.background = 'var(--color-teal-dark)';
      CartDrawer.open();

      setTimeout(() => {
        btn.innerHTML = originalHTML;
        btn.style.background = '';
        btn.disabled = false;
      }, 2200);
    }
  } catch (err) {
    showToast(err.message);
    btn.innerHTML = originalHTML;
    btn.disabled = false;
  }
}

/* ─── Nav / hamburger ────────────────────────────────────────────────────── */
function initNav() {
  const hamburger = $('#NavHamburger');
  const mobileNav = $('#NavMobile');
  const cartBtns  = $$('[data-open-cart]');

  hamburger?.addEventListener('click', () => {
    const open = hamburger.classList.toggle('open');
    mobileNav?.classList.toggle('open', open);
    hamburger.setAttribute('aria-expanded', open);
  });

  mobileNav?.addEventListener('click', e => {
    if (e.target.closest('a')) {
      mobileNav.classList.remove('open');
      hamburger?.classList.remove('open');
      hamburger?.setAttribute('aria-expanded', 'false');
    }
  });

  cartBtns.forEach(btn => btn.addEventListener('click', () => CartDrawer.open()));
}

/* ─── Review Carousel ────────────────────────────────────────────────────── */
function initCarousels() {
  $$('[data-carousel]').forEach(carousel => {
    const track  = $('[data-carousel-track]', carousel);
    const prev   = $('[data-carousel-prev]', carousel);
    const next   = $('[data-carousel-next]', carousel);
    const dotsEl = $('[data-carousel-dots]', carousel);

    if (!track) return;

    const cards = $$('.review-card', track);
    if (cards.length === 0) return;

    let perPage = window.innerWidth < 768 ? 1 : window.innerWidth < 1024 ? 2 : 3;
    let page = 0;
    const pages = () => Math.max(1, Math.ceil(cards.length / perPage));

    function render() {
      const cardW = cards[0].offsetWidth + 20;
      track.style.transform = `translateX(-${page * perPage * cardW}px)`;
      if (prev) prev.disabled = page === 0;
      if (next) next.disabled = page >= pages() - 1;
      if (dotsEl) {
        dotsEl.innerHTML = Array.from({ length: pages() }, (_, i) =>
          `<button class="carousel__dot${i === page ? ' active' : ''}" aria-label="Page ${i + 1}"></button>`
        ).join('');
        $$('.carousel__dot', dotsEl).forEach((dot, i) =>
          dot.addEventListener('click', () => { page = i; render(); })
        );
      }
    }

    prev?.addEventListener('click', () => { if (page > 0) { page--; render(); } });
    next?.addEventListener('click', () => { if (page < pages() - 1) { page++; render(); } });

    // Touch swipe
    let touchStartX = 0;
    track.addEventListener('touchstart', e => { touchStartX = e.touches[0].clientX; }, { passive: true });
    track.addEventListener('touchend', e => {
      const dx = e.changedTouches[0].clientX - touchStartX;
      if (Math.abs(dx) > 40) {
        if (dx < 0 && page < pages() - 1) { page++; render(); }
        if (dx > 0 && page > 0) { page--; render(); }
      }
    }, { passive: true });

    window.addEventListener('resize', () => {
      const newPer = window.innerWidth < 768 ? 1 : window.innerWidth < 1024 ? 2 : 3;
      if (newPer !== perPage) { perPage = newPer; page = 0; }
      render();
    });

    render();
  });
}

/* ─── FAQ Accordion ──────────────────────────────────────────────────────── */
function initFAQ() {
  $$('.faq-question').forEach(btn => {
    btn.addEventListener('click', () => {
      const item = btn.closest('.faq-item');
      const answer = $('.faq-answer', item);
      const open = item.classList.toggle('open');
      btn.setAttribute('aria-expanded', open);
      if (answer) {
        answer.style.maxHeight = open ? answer.scrollHeight + 'px' : '0';
      }
    });
  });
}

/* ─── Product form (PDP) ─────────────────────────────────────────────────── */
function initProductForm() {
  $$('[data-add-to-cart]').forEach(btn => {
    btn.addEventListener('click', () => handleAddToCart(btn));
  });
}

/* ─── Sticky buy bar ─────────────────────────────────────────────────────── */
function initStickyBuyBar() {
  const stickyBar = document.querySelector('.sticky-buy');
  if (!stickyBar) return;

  function updateStickyBar() {
    const scrolled = window.scrollY;
    const pageHeight = document.documentElement.scrollHeight - window.innerHeight;
    const halfwayPoint = pageHeight * 0.5;

    if (scrolled >= halfwayPoint) {
      stickyBar.style.display = 'flex';
    } else {
      stickyBar.style.display = 'none';
    }
  }

  stickyBar.style.display = 'none';

  window.addEventListener('scroll', updateStickyBar, { passive: true });
  updateStickyBar();

  const stickyBtn = stickyBar.querySelector('[data-add-to-cart]');
  stickyBtn?.addEventListener('click', () => handleAddToCart(stickyBtn));
}

/* ─── Smooth anchor scroll ───────────────────────────────────────────────── */
function initSmoothScroll() {
  document.addEventListener('click', e => {
    const link = e.target.closest('a[href^="#"]');
    if (!link) return;
    const id = link.getAttribute('href').slice(1);
    if (!id) return;
    const target = document.getElementById(id);
    if (!target) {
      e.preventDefault();
      window.location.href = '/' + link.getAttribute('href');
      return;
    }
    e.preventDefault();
    const start    = window.scrollY;
    const navH     = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--nav-h')) || 68;
    const end      = target.getBoundingClientRect().top + start - navH;
    const duration = 1000;
    let startTime  = null;
    function easeInOut(t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }
    function step(now) {
      if (!startTime) startTime = now;
      const elapsed  = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      window.scrollTo(0, start + (end - start) * easeInOut(progress));
      if (progress < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  });
}

/* ─── Flipbook ───────────────────────────────────────────────────────────── */
function initFlipbook() {
  const track = document.getElementById('FlipbookTrack');
  if (!track) return;

  const slides = Array.from(track.querySelectorAll('.flipbook-slide'));
  if (slides.length === 0) return;

  const prevBtn   = document.getElementById('FlipPrev');
  const nextBtn   = document.getElementById('FlipNext');
  const currentEl = document.getElementById('FlipCurrent');
  const totalEl   = document.getElementById('FlipTotal');

  let current = 0;
  totalEl.textContent = slides.length;

  function updateSlides() {
    slides.forEach((slide, i) => {
      const offset = i - current;
      slide.classList.remove('is-active', 'is-prev-1', 'is-prev-2', 'is-next-1', 'is-next-2', 'is-hidden');
      if (offset === 0)       slide.classList.add('is-active');
      else if (offset === -1) slide.classList.add('is-prev-1');
      else if (offset === -2) slide.classList.add('is-prev-2');
      else if (offset === 1)  slide.classList.add('is-next-1');
      else if (offset === 2)  slide.classList.add('is-next-2');
      else                    slide.classList.add('is-hidden');
    });
    currentEl.textContent = current + 1;
    prevBtn.disabled = current === 0;
    nextBtn.disabled = current === slides.length - 1;
  }

  slides.forEach((slide, i) => {
    slide.addEventListener('click', () => {
      if (i !== current) { current = i; updateSlides(); }
    });
  });

  prevBtn.addEventListener('click', () => {
    if (current > 0) { current--; updateSlides(); }
  });

  nextBtn.addEventListener('click', () => {
    if (current < slides.length - 1) { current++; updateSlides(); }
  });

  let touchStartX = 0;
  track.addEventListener('touchstart', e => { touchStartX = e.touches[0].clientX; }, { passive: true });
  track.addEventListener('touchend', e => {
    const diff = touchStartX - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0 && current < slides.length - 1) { current++; updateSlides(); }
      if (diff < 0 && current > 0) { current--; updateSlides(); }
    }
  }, { passive: true });

  updateSlides();
}

/* ─── Policy Modals ──────────────────────────────────────────────────────── */
function initPolicyModals() {
  // Start with all modal focusables inert
  document.querySelectorAll('.policy-modal').forEach(modal => {
    modal.querySelectorAll('a, button').forEach(el => el.setAttribute('tabindex', '-1'));
  });

  function openModal(id) {
    const modal = document.getElementById('modal-' + id);
    if (!modal) return;
    modal.setAttribute('aria-hidden', 'false');
    modal.querySelectorAll('a, button').forEach(el => el.removeAttribute('tabindex'));
    document.body.style.overflow = 'hidden';
  }
  function closeModal(modal) {
    modal.setAttribute('aria-hidden', 'true');
    modal.querySelectorAll('a, button').forEach(el => el.setAttribute('tabindex', '-1'));
    document.body.style.overflow = '';
  }

  document.addEventListener('click', e => {
    const trigger = e.target.closest('[data-modal]');
    if (trigger) { e.preventDefault(); openModal(trigger.dataset.modal); return; }

    const closeBtn = e.target.closest('[data-modal-close]');
    if (closeBtn) { closeModal(closeBtn.closest('.policy-modal')); return; }
  });

  document.addEventListener('keydown', e => {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('.policy-modal[aria-hidden="false"]').forEach(closeModal);
  });
}

/* ─── Flipbook lightbox ───────────────────────────────────────────────────── */
function initFlipbookLightbox() {
  const track = document.getElementById('FlipbookTrack');
  if (!track) return;

  const slides = Array.from(track.querySelectorAll('.flipbook-slide'));
  if (slides.length === 0) return;

  const lb = document.createElement('div');
  lb.id = 'FlipbookLightbox';
  lb.className = 'flipbook-lightbox';
  lb.setAttribute('aria-hidden', 'true');
  lb.innerHTML = `
    <div class="flipbook-lightbox__overlay"></div>
    <button class="flipbook-lightbox__close" aria-label="Close fullscreen">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
    </button>
    <button class="flipbook-lightbox__prev" aria-label="Previous page">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="15 18 9 12 15 6"/></svg>
    </button>
    <div class="flipbook-lightbox__img-wrap">
      <img class="flipbook-lightbox__img" src="" alt="Preview page" id="FlipbookLightboxImg">
    </div>
    <button class="flipbook-lightbox__next" aria-label="Next page">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 18 15 12 9 6"/></svg>
    </button>
    <p class="flipbook-lightbox__counter"><span id="LBCurrent">1</span> / <span id="LBTotal">1</span></p>
  `;
  document.body.appendChild(lb);

  const lbImg = document.getElementById('FlipbookLightboxImg');
  const lbCurrent = document.getElementById('LBCurrent');
  const lbTotal = document.getElementById('LBTotal');
  const closeBtn = lb.querySelector('.flipbook-lightbox__close');
  const prevBtn = lb.querySelector('.flipbook-lightbox__prev');
  const nextBtn = lb.querySelector('.flipbook-lightbox__next');
  const overlay = lb.querySelector('.flipbook-lightbox__overlay');

  let currentIndex = 0;

  function getPageSrcs() {
    return slides.map(s => {
      const img = s.querySelector('img');
      return img ? img.src : '';
    }).filter(Boolean);
  }

  function showLightboxPage(index, srcs) {
    srcs = srcs || getPageSrcs();
    currentIndex = Math.max(0, Math.min(index, srcs.length - 1));
    lbImg.src = srcs[currentIndex];
    lbCurrent.textContent = currentIndex + 1;
    prevBtn.disabled = currentIndex === 0;
    nextBtn.disabled = currentIndex === srcs.length - 1;
  }

  function openLightbox(index) {
    const srcs = getPageSrcs();
    if (srcs.length === 0) return;
    lbTotal.textContent = srcs.length;
    showLightboxPage(index, srcs);
    lb.setAttribute('aria-hidden', 'false');
    lb.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  function closeLightbox() {
    lb.setAttribute('aria-hidden', 'true');
    lb.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  slides.forEach((slide, i) => {
    slide.style.cursor = 'zoom-in';
    slide.addEventListener('click', () => openLightbox(i));
  });

  closeBtn.addEventListener('click', closeLightbox);
  overlay.addEventListener('click', closeLightbox);
  prevBtn.addEventListener('click', () => showLightboxPage(currentIndex - 1));
  nextBtn.addEventListener('click', () => showLightboxPage(currentIndex + 1));

  document.addEventListener('keydown', e => {
    if (!lb.classList.contains('is-open')) return;
    if (e.key === 'Escape') closeLightbox();
    if (e.key === 'ArrowLeft') showLightboxPage(currentIndex - 1);
    if (e.key === 'ArrowRight') showLightboxPage(currentIndex + 1);
  });

  let lbTouchX = 0;
  lb.addEventListener('touchstart', e => { lbTouchX = e.touches[0].clientX; }, { passive: true });
  lb.addEventListener('touchend', e => {
    const diff = lbTouchX - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) showLightboxPage(currentIndex + 1);
      else showLightboxPage(currentIndex - 1);
    }
  }, { passive: true });
}

/* ─── Spin animation ─────────────────────────────────────────────────────── */
const spinStyle = document.createElement('style');
spinStyle.textContent = `@keyframes spin{to{transform:rotate(360deg)}}.spin{animation:spin .8s linear infinite}`;
document.head.appendChild(spinStyle);

/* ─── Load More Reviews ──────────────────────────────────────────────────── */
function initLoadMore() {
  const loadMoreBtn = document.querySelector('.review-load-more .btn');
  if (!loadMoreBtn) return;

  const allItems = Array.from(document.querySelectorAll('.review-item'));
  const hiddenItems = () => Array.from(document.querySelectorAll('.review-item--hidden'));
  const CHUNK = 3;
  const INITIAL = 3;

  function updateButton() {
    const remaining = hiddenItems().length;
    const totalVisible = allItems.length - remaining;

    if (remaining === 0 && totalVisible > INITIAL) {
      loadMoreBtn.textContent = 'See Less Reviews';
    } else if (remaining > 0) {
      loadMoreBtn.textContent = 'Load more reviews';
    } else {
      loadMoreBtn.style.display = 'none';
    }
  }

  loadMoreBtn.addEventListener('click', () => {
    const hidden = hiddenItems();

    if (loadMoreBtn.textContent.trim() === 'See Less Reviews') {
      allItems.forEach((item, i) => {
        if (i >= INITIAL) item.classList.add('review-item--hidden');
      });
    } else {
      hidden.slice(0, CHUNK).forEach(item => item.classList.remove('review-item--hidden'));
    }

    updateButton();
  });

  updateButton();
}

/* ─── Boot ───────────────────────────────────────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {
  CartDrawer.init();
  initNav();
  initCarousels();
  initFAQ();
  initProductForm();
  initStickyBuyBar();
  initSmoothScroll();
  initPolicyModals();
  initFlipbook();
  initFlipbookLightbox();
  initLoadMore();

  // Refresh badge on page load
  Cart.get().then(cart => CartDrawer._updateBadge(cart.item_count)).catch(() => {});
});
